import { useCreateOrganization, useUserInfo } from "@/api/user";
import { ApiError } from "@/api/utils";
import { ROUTES } from "@/constants/routes";
import { CenteredLayout } from "@/layouts/CenteredLayout";
import { nativeLoginOpts } from "@/native/auth";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import * as analytics from "@/utils/analytics";
import { useAuth0 } from "@auth0/auth0-react";
import {
  clearSignupUtm,
  consumeTemplateSignupIntent,
  parseSignupUtm,
  parseTemplateSignupIntent,
  peekSignupUtm,
  Spinner,
} from "@cohesive/ui-components";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

/** `ZenseiApiErrorBody` plus the machine-readable refusal code. */
type TemplateSetupErrorBody = {
  error?: { message?: string; code?: string };
};

export function TemplateSetupPage() {
  const navigate = useNavigate();
  const { user, loginWithRedirect } = useAuth0();
  const { data: userInfo, isLoading } = useUserInfo();
  const startedRef = useRef(false);

  const createOrganizationMutation = useCreateOrganization({
    onSuccess: async (organization) => {
      clearSignupUtm();
      analytics.trackOrganizationCreated({
        organization,
        signupSource: "public_template",
      });

      await loginWithRedirect({
        authorizationParams: { organization: organization.auth0_id },
        appState: { returnTo: ROUTES.HOME },
        ...nativeLoginOpts(),
      });
    },
    onError: (error) => {
      // Flag-off and the per-IP cap are expected refusals, not faults; both fall
      // back to the manual form. Only surface a message we didn't ask for.
      const apiError =
        error instanceof ApiError
          ? (error.data as TemplateSetupErrorBody | undefined)?.error
          : undefined;
      const refused = apiError?.code === "signup_source_refused";
      if (!refused && apiError?.message) {
        toast.error(apiError.message);
      }
      navigate(ROUTES.ONBOARDING.CREATE_ORGANIZATION, { replace: true });
    },
  });

  useEffect(() => {
    if (isLoading || startedRef.current) return;
    startedRef.current = true;

    // Consume unconditionally: whichever branch wins, the stash is spent.
    const stashed = consumeTemplateSignupIntent();
    const intent = parseTemplateSignupIntent(window.location.search) ?? stashed;
    const utm = parseSignupUtm(window.location.search) ?? peekSignupUtm();

    if ((userInfo?.organizations?.length ?? 0) > 0) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }

    if (!intent) {
      navigate(ROUTES.ONBOARDING.CREATE_ORGANIZATION, { replace: true });
      return;
    }

    createOrganizationMutation.mutate({
      signup_source: "public_template",
      billing_email: user?.email,
      template_prompt: {
        kind: intent.kind,
        id: intent.id,
        title: intent.title,
        author_name: intent.author,
        cta: intent.cta,
        created_at: new Date(intent.ts ?? Date.now()).toISOString(),
      },
      ...(utm ? { utm } : {}),
    });
  }, [isLoading, userInfo, user, navigate, createOrganizationMutation]);

  return (
    <>
      <title>Setting up your workspace</title>

      <CenteredLayout maxWidth="md">
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <LogoIcon width="64" height="64" />
          </div>
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground tracking-medium">
              Setting up your workspace
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium tracking-tight">
              This only takes a moment.
            </p>
          </div>
          <div
            data-testid="template-setup-spinner"
            className="flex justify-center"
          >
            <Spinner size="xl" />
          </div>
        </div>
      </CenteredLayout>
    </>
  );
}
